// ============================================
// Three.js Renderer Module
// Main entry point for Three.js rendering
// ============================================

import { CONFIG } from '../../config/game-config';
import { getCurrentStageCfg } from './background';
import {
    getGameState,
    getIsStageTransitioning,
    getStageTransitionTimer,
    getBgPlanetY
} from '../../store/index';

// Import from modular Three.js components
import {
    // Scene management
    getThreeInitialized,
    getThreeRenderer,
    getThreeScene,
    getThreeCamera,
    initThreeScene,
    renderThreeScene,
    disposeThreeScene,

    // Background gradient
    initThreeBackground,
    updateThreeBackgroundColors,
    updateBackgroundPosition,
    disposeThreeBackground,

    // Nebula
    createThreeNebula,
    updateThreeNebula,
    disposeThreeNebula,

    // Earth
    createThreeEarth,
    updateThreeEarth,
    disposeThreeEarth
} from './three';

// Re-export for backward compatibility
export { resetEarthRotation } from './three';
export { buildNebulaPattern } from './three';

// ============================================
// Public API - Getters
// ============================================

export { getThreeInitialized, getThreeRenderer, getThreeScene, getThreeCamera };

// ============================================
// initThreeJS() - Initialize Three.js scene
// ============================================
export function initThreeJS(): void {
    // Initialize core scene
    initThreeScene();

    // Initialize background gradient
    initThreeBackground();

    // Create nebula (for Deep Space)
    createThreeNebula();

    // Create Earth (Low Orbit)
    createThreeEarth();
}

// ============================================
// updateThreeJS() - Three.js frame update
// ============================================
export function updateThreeJS(shakeX = 0, shakeY = 0): void {
    if (!getThreeInitialized()) {
        return;
    }

    // Get current state
    const stageCfg = getCurrentStageCfg();
    const gameState = getGameState();
    const isStageTransitioning = getIsStageTransitioning();
    const stageTransitionTimer = getStageTransitionTimer();
    const bgPlanetY = getBgPlanetY();

    // Calculate transition progress
    let transitionProgress = 1;
    if (isStageTransitioning) {
        transitionProgress = 1 - (stageTransitionTimer / CONFIG.stages.transitionDuration);
    }

    // Update background colors
    updateThreeBackgroundColors(stageCfg, transitionProgress);

    // Update background position (shake effect)
    updateBackgroundPosition(shakeX, shakeY);

    // Determine active states
    const isPlaying = gameState === 'playing' || gameState === 'gameover';
    const isLowOrbit = stageCfg && stageCfg.specialEffect === 'lowOrbit';
    const isDeepSpace = stageCfg && stageCfg.specialEffect === 'deepSpace';

    // Update nebula (Deep Space stage)
    const showNebula = isDeepSpace && isPlaying;
    updateThreeNebula(showNebula, shakeX);

    // Update Earth (Low Orbit stage)
    const showEarth = isLowOrbit && isPlaying;
    updateThreeEarth(showEarth, bgPlanetY, shakeX, shakeY);

    // Render
    renderThreeScene();
}

// ============================================
// disposeThreeJS() - Dispose all Three.js resources
// ============================================
export function disposeThreeJS(): void {
    // Dispose in reverse order of creation
    disposeThreeEarth();
    disposeThreeNebula();
    disposeThreeBackground();
    disposeThreeScene();
}

// Legacy exports for backward compatibility
export { initThreeBackground, createThreeNebula, createThreeEarth };
export { updateThreeBgTexture, updateThreeBackgroundColors } from './three';
